import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { KassenApi, VorschlagUebernahme } from '../api';
import { BEDIENER_EREIGNIS, ladeAnmeldung } from '../bediener';
import { kassenLagerSender } from '../hintergrund';
import { neueVorgangsId } from '../bonierung';
import { VorgangsKennung } from '../vorgangskennung';
import {
  alteVorgangsId,
  ladeErfassung,
  leseMenge,
  listeNachUebergabe,
  mengeSetzen,
  mitKennung,
  pruefeCode,
  scanHinzufuegen,
  speichereErfassung,
  vergissAlteVorgangsId,
  zeileEntfernen,
  fehlerZurueckEinmal,
  type ErfassteZeile,
  type Erfassung,
  type ErfassungsArt,
  ankerAus,
  inventurZiel,
  type InventurStand,
  type LaufAnker,
} from '../lager';
import {
  erstelleAuftrag,
  fehlerzeilen,
  inhaltText,
  type LaufErgebnis,
  type Sender,
} from '../lager-warteschlange';
import { formatiereEuro, parseEuro, STEUERSAETZE, type Steuersatz } from '../warenkorb';
import { KameraScanner } from './KameraScanner';
import {
  LagerBuchungen,
  listeBearbeitbar,
  useLagerWarteschlange,
  useListenSperre,
} from './LagerBuchungen';

function mengeText(m: number): string {
  return String(m).replace('.', ',');
}

/**
 * Reiter „Lager“ (Modul Lager): Wareneingang und Inventur per Scanner, MDE-Gerät
 * oder Kamera. Scanner im Tastaturmodus tippen in das Codefeld und schließen mit
 * Enter ab. Bekannte Artikel werden beim Buchen gebucht, unbekannte Codes landen
 * als Vorschlag unten in der Liste und werden dort mit Preis übernommen.
 */
/** So lange gilt ein geladener Laufstand als Anker für den ersten Scan einer neuen Liste. */
const ANKER_GUELTIG_MS = 10 * 60_000;

export function Lager(props: { api: KassenApi; kassenId: string }) {
  const { api, kassenId } = props;
  const [erfassung, setErfassung] = useState<Erfassung>(() =>
    mitKennung(ladeErfassung(localStorage, kassenId)),
  );
  const [code, setCode] = useState('');
  const [mengeEingabe, setMengeEingabe] = useState('1');
  const [kamera, setKamera] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [vorschlaege, setVorschlaege] = useState<VorschlagZeile[]>([]);
  const [gruppen, setGruppen] = useState<string[]>([]);
  const [gruppenUst, setGruppenUst] = useState<Record<string, string | null>>({});
  const codeFeld = useRef<HTMLInputElement>(null);

  // Inventurlauf: Laufstand frisch vom Server (Anzeige, Laufanker neuer Listen, Entscheidung
  // beim Buchen – nie über einen Lauf hinweg; dieselben Regeln prüft der Server).
  const [lauf, setLauf] = useState<InventurStand['offen']>(null);
  const standCache = useRef<{ stand: InventurStand; am: number } | null>(null);
  const laufstandLaden = useCallback(async (): Promise<InventurStand | null> => {
    try {
      const stand = await api.inventurStand(kassenId);
      standCache.current = { stand, am: Date.now() };
      setLauf(stand.offen);
      return stand;
    } catch {
      return null;
    }
  }, [api, kassenId]);
  useEffect(() => {
    void laufstandLaden();
    const t = setInterval(() => {
      void laufstandLaden();
    }, 60_000);
    return () => {
      clearInterval(t);
    };
  }, [laufstandLaden]);
  const ankerFuerNeueListe = (): LaufAnker | undefined => {
    const c = standCache.current;
    const alter = c === null ? -1 : Date.now() - c.am;
    return c !== null && alter >= 0 && alter <= ANKER_GUELTIG_MS ? ankerAus(c.stand) : undefined;
  };

  // Nur ein Tab bearbeitet die Liste; ein zweiter wartet und lädt sie bei Übernahme neu.
  const sperre = useListenSperre(`kasse:${kassenId}`, () => {
    setErfassung(mitKennung(ladeErfassung(localStorage, kassenId)));
  });
  const gesperrt = !listeBearbeitbar(sperre);
  // Ohne Web Locks: Änderungen anderer Tabs übernehmen (zuletzt geschrieben gilt).
  useEffect(() => {
    if (sperre !== 'unbekannt') {
      return undefined;
    }
    const geaendert = (e: StorageEvent): void => {
      if (e.key === `bilanx.erfassung.${kassenId}`) {
        setErfassung(mitKennung(ladeErfassung(localStorage, kassenId)));
      }
    };
    window.addEventListener('storage', geaendert);
    return () => {
      window.removeEventListener('storage', geaendert);
    };
  }, [sperre, kassenId]);
  useEffect(() => {
    if (!gesperrt) {
      speichereErfassung(localStorage, kassenId, erfassung);
    }
  }, [kassenId, erfassung, gesperrt]);

  const vorschlaegeLaden = useCallback(async () => {
    try {
      const v = await api.artikelvorschlaege(kassenId);
      setVorschlaege(v.vorschlaege);
      setGruppen(v.gruppen);
      setGruppenUst(v.gruppenSteuersatz ?? {});
    } catch {
      // offline oder Modul nicht gebucht – Liste bleibt leer
    }
  }, [api, kassenId]);
  useEffect(() => {
    void vorschlaegeLaden();
  }, [vorschlaegeLaden]);

  // Buchungsaufträge mit dauerhafter Vorgangskennung (IndexedDB) – siehe lager-warteschlange.ts
  const kontext = `kasse:${kassenId}`;
  // Gleicher Absender wie die app-weite Nachsendung (hintergrund.ts, P8)
  const sende = useMemo<Sender>(() => kassenLagerSender(api, kassenId), [api, kassenId]);
  const nachLauf = useCallback(
    (r: LaufErgebnis) => {
      if (r.gebucht > 0) {
        void vorschlaegeLaden();
      }
    },
    [vorschlaegeLaden],
  );
  const schlange = useLagerWarteschlange(kontext, sende, nachLauf);

  // Wegen Anmeldung/Recht (403) oder Zugang (401) wartende Aufträge beim Öffnen des
  // Reiters und nach jedem Bedienerwechsel erneut versuchen.
  const { freigeben } = schlange;
  useEffect(() => {
    void freigeben(['zugang', 'recht']);
    const wechsel = (): void => {
      void freigeben(['recht']);
    };
    window.addEventListener(BEDIENER_EREIGNIS, wechsel);
    return () => {
      window.removeEventListener(BEDIENER_EREIGNIS, wechsel);
    };
  }, [freigeben]);

  // Liste schon übergeben (Absturz zwischen Ablage und Leeren, zweiter Tab)? Dann genau
  // die übergebenen Mengen abziehen und eine neue Kennung vergeben – nie zweimal buchen.
  const { finde } = schlange;
  const listenKennung = erfassung.vorgangId;
  useEffect(() => {
    if (listenKennung === undefined) {
      return undefined;
    }
    let aktiv = true;
    void finde(listenKennung).then((a) => {
      if (aktiv && a !== null) {
        setErfassung((e) => (e.vorgangId === listenKennung ? listeNachUebergabe(e, a) : e));
      }
    });
    return () => {
      aktiv = false;
    };
  }, [finde, listenKennung]);

  /** Code übernehmen: nachschlagen (wenn online), dann in die Liste. */
  const erfasse = useCallback(
    async (roh: string) => {
      if (gesperrt) {
        setFehler('Die Liste ist in einem anderen Fenster geöffnet – dort weiterarbeiten.');
        return;
      }
      const ean = pruefeCode(roh);
      if (ean === null) {
        setFehler(`„${roh}“ ist kein Code (1–48 Zeichen ohne Leerzeichen)`);
        return;
      }
      const menge = leseMenge(mengeEingabe);
      if (menge === null || menge <= 0) {
        setFehler('Menge: Zahl größer 0, z. B. 12 oder 2,5');
        return;
      }
      setFehler(null);
      let zeile: Omit<ErfassteZeile, 'menge'> = {
        code: ean,
        bezeichnung: '',
        bekannt: false,
        bestand: null,
      };
      if (navigator.onLine) {
        try {
          // Gleiche Zuordnung wie Handterminal und Kassen-Scanner (lagercode.ts)
          const a = await api.lagerCodeAuskunft(kassenId, ean);
          if (a.status === 'mehrdeutig') {
            setFehler(
              `${ean} passt zu mehreren Artikeln (${(a.mehrdeutig ?? [])
                .map((m) => m.bezeichnung)
                .join(', ')}) – Code im Backoffice bereinigen`,
            );
            return;
          }
          zeile = {
            code: ean,
            bezeichnung:
              a.artikel?.bezeichnung ?? a.vorschlag?.bezeichnung ?? a.produkt?.bezeichnung ?? '',
            bekannt: a.artikel !== null,
            bestand: a.artikel?.bestand ?? null,
            sperrgrund: a.artikel?.gesperrt === true ? (a.artikel.sperrgrund ?? 'gesperrt') : null,
          };
        } catch {
          // offline – wird beim Buchen geklärt
        }
      }
      const anker = ankerFuerNeueListe();
      setErfassung((e) => scanHinzufuegen(e, zeile, menge, anker));
      setMengeEingabe('1');
    },
    [api, kassenId, mengeEingabe, gesperrt],
  );

  /**
   * Buchen = Liste als unveränderlichen Auftrag mit dauerhafter Vorgangskennung ablegen
   * (zuerst IndexedDB, dann Liste leeren) und senden. Geht die Antwort verloren, sendet
   * die Warteschlange denselben Auftrag erneut – der Server bucht ihn nicht doppelt.
   */
  const buchenLaeuft = useRef(false);
  async function buchen(): Promise<void> {
    if (buchenLaeuft.current) {
      return;
    }
    buchenLaeuft.current = true;
    try {
      await buchenGesperrt();
    } finally {
      buchenLaeuft.current = false;
    }
  }

  async function buchenGesperrt(): Promise<void> {
    // Inventur: Ziel nur mit frisch geladenem Laufstand (freie Inventur oder Lauf-Zählung)
    let laufId: number | null = null;
    if (erfassung.art === 'inventur') {
      setLaeuft(true);
      const stand = await laufstandLaden();
      setLaeuft(false);
      if (stand === null) {
        setFehler(
          'Ob ein Inventurlauf offen ist, lässt sich gerade nicht prüfen (keine Verbindung). Die Liste bleibt gespeichert – mit Verbindung erneut buchen.',
        );
        return;
      }
      const ziel = inventurZiel(stand, erfassung.laufAnker);
      if (ziel.art === 'gesperrt') {
        setFehler(ziel.grund);
        return;
      }
      if (ziel.art === 'lauf') {
        if (erfassung.bezug.trim() === '') {
          setFehler(
            'Zählstelle angeben (z. B. Regal 3) – im Inventurlauf zählt jede Stelle für sich',
          );
          return;
        }
        if (
          !window.confirm(
            `Zählung der Zählstelle „${erfassung.bezug.trim()}“ an Inventurlauf ${String(ziel.laufId)} übergeben? Den Bestand setzt erst der Abschluss im Backoffice.`,
          )
        ) {
          return;
        }
        laufId = ziel.laufId;
      }
    }
    setLaeuft(true);
    setFehler(null);
    setMeldung(null);
    try {
      if (gesperrt) {
        throw new Error('Die Liste ist in einem anderen Fenster geöffnet');
      }
      let liste = mitKennung(erfassung);
      // Übergang: eine schon unterwegs gewesene Kennung der alten Sitzungslogik weiterverwenden
      const altePositionen = liste.zeilen.map((z) => ({
        code: z.code,
        menge: z.menge,
        ...(z.bekannt || z.bezeichnung.trim() === '' ? {} : { bezeichnung: z.bezeichnung.trim() }),
      }));
      const alteId = alteVorgangsId(
        sessionStorage,
        kassenId,
        inhaltText(liste.art, liste.bezug, altePositionen),
      );
      if (alteId !== null && alteId !== liste.vorgangId) {
        // Die alte Kennung wird die Kennung der Liste – so findet ein Neustart den Auftrag.
        const vorher = liste.vorgangId;
        liste = { ...liste, vorgangId: alteId };
        setErfassung((e) => (e.vorgangId === vorher ? { ...e, vorgangId: alteId } : e));
      }
      // Liste samt Kennung sofort sichern, bevor der Auftrag abgelegt wird
      speichereErfassung(localStorage, kassenId, liste);
      const auftrag = erstelleAuftrag(
        liste,
        kontext,
        new Date(),
        ladeAnmeldung(sessionStorage, kassenId)?.name ?? null,
        neueVorgangsId,
        null,
        laufId,
      );
      const gespeichert = await schlange.einreihen(auftrag);
      // Erst nach der dauerhaften Ablage die alte Sitzungskennung vergessen
      vergissAlteVorgangsId(sessionStorage, kassenId);
      // Vom aktuellen Stand genau die übergebenen Mengen abziehen: was währenddessen
      // gescannt wurde, bleibt stehen (neue Kennung für den Rest).
      const kennung = liste.vorgangId;
      setErfassung((e) => (e.vorgangId === kennung ? listeNachUebergabe(e, gespeichert) : e));
      setMeldung(
        navigator.onLine
          ? 'Buchung übergeben – Stand siehe „Buchungen“'
          : 'Offline gespeichert – wird bei Verbindung automatisch gebucht',
      );
    } catch (e: unknown) {
      setFehler(
        `Buchung nicht gespeichert, die Liste bleibt: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setLaeuft(false);
      codeFeld.current?.focus();
    }
  }

  function artWechseln(art: ErfassungsArt): void {
    if (
      erfassung.zeilen.length > 0 &&
      art !== erfassung.art &&
      !window.confirm('Die Liste ist noch nicht gebucht. Trotzdem umschalten?')
    ) {
      return;
    }
    setErfassung({ ...erfassung, art });
  }

  const summeMenge = erfassung.zeilen.reduce((s, z) => s + z.menge, 0);

  return (
    <>
      <div className="karte">
        <div className="zeile" style={{ alignItems: 'flex-end' }}>
          <div className="chips" role="group" aria-label="Art der Erfassung">
            <button
              type="button"
              className={erfassung.art === 'wareneingang' ? 'aktiv' : ''}
              onClick={() => {
                artWechseln('wareneingang');
              }}
            >
              Wareneingang
            </button>
            <button
              type="button"
              className={erfassung.art === 'inventur' ? 'aktiv' : ''}
              onClick={() => {
                artWechseln('inventur');
              }}
            >
              Inventur
            </button>
          </div>
          <label>
            {erfassung.art === 'wareneingang'
              ? 'Lieferschein'
              : lauf !== null
                ? 'Zählstelle'
                : 'Zählliste'}
            <input
              value={erfassung.bezug}
              maxLength={200}
              placeholder={erfassung.art === 'wareneingang' ? 'z. B. LS-4711' : 'z. B. Regal 3'}
              onChange={(e) => {
                setErfassung({ ...erfassung, bezug: e.target.value });
              }}
            />
          </label>
        </div>
        <p className="hinweis">
          {erfassung.art === 'wareneingang'
            ? 'Jeder Scan erhöht die Menge. Unbekannte Codes werden als neue Artikel vorgeschlagen.'
            : lauf !== null
              ? `Inventurlauf ${String(lauf.id)} „${lauf.bezeichnung}“ ist offen: gezählte Menge je Artikel für die Zählstelle oben; den Bestand setzt erst der Abschluss im Backoffice.`
              : 'Gezählte Menge je Artikel – beim Buchen wird der Bestand darauf gesetzt.'}
        </p>
        <form
          className="erfassung-scan"
          onSubmit={(e) => {
            e.preventDefault();
            const c = code.trim();
            setCode('');
            if (c !== '') {
              void erfasse(c);
            }
          }}
        >
          <label className="scan-menge">
            Menge
            <input
              inputMode="decimal"
              value={mengeEingabe}
              onChange={(e) => {
                setMengeEingabe(e.target.value);
              }}
            />
          </label>
          <label className="scan-code">
            Code scannen oder eintippen
            <input
              ref={codeFeld}
              autoFocus
              inputMode="numeric"
              autoComplete="off"
              value={code}
              placeholder="Scanner hier – Enter übernimmt"
              onChange={(e) => {
                setCode(e.target.value);
              }}
            />
          </label>
          <button type="submit" className="taste zweit">
            Übernehmen
          </button>
          <button
            type="button"
            className="taste"
            onClick={() => {
              setKamera(true);
            }}
          >
            Kamera
          </button>
        </form>
        {gesperrt && (
          <p className="warnung">
            Diese Liste ist in einem anderen Fenster geöffnet (oder wird gerade geladen). Hier erst
            weiterarbeiten, wenn das andere Fenster geschlossen ist.
          </p>
        )}
        {fehler !== null && <p className="fehler">{fehler}</p>}
        {meldung !== null && <p className="hinweis">{meldung}</p>}
      </div>

      <div className="karte">
        <h2>
          {erfassung.art === 'wareneingang' ? 'Wareneingang' : 'Inventur'} ·{' '}
          {erfassung.zeilen.length} Artikel · {mengeText(Math.round(summeMenge * 1000) / 1000)}{' '}
          Einheiten
        </h2>
        {erfassung.zeilen.length === 0 ? (
          <p className="hinweis">Noch nichts erfasst.</p>
        ) : (
          <table className="liste">
            <thead>
              <tr>
                <th>Code</th>
                <th>Artikel</th>
                <th>Bestand</th>
                <th>{erfassung.art === 'wareneingang' ? 'Zugang' : 'Gezählt'}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {erfassung.zeilen.map((z) => (
                <tr key={z.code}>
                  <td>{z.code}</td>
                  <td>
                    {z.bekannt ? (
                      <>
                        {z.bezeichnung}
                        {z.sperrgrund != null && (
                          <span className="neu"> · Verkauf gesperrt: {z.sperrgrund}</span>
                        )}
                      </>
                    ) : (
                      <>
                        <input
                          value={z.bezeichnung}
                          placeholder="Bezeichnung (neu)"
                          maxLength={120}
                          onChange={(e) => {
                            setErfassung((alt) => ({
                              ...alt,
                              zeilen: alt.zeilen.map((x) =>
                                x.code === z.code ? { ...x, bezeichnung: e.target.value } : x,
                              ),
                            }));
                          }}
                        />{' '}
                        <span className="neu">
                          {erfassung.art === 'wareneingang'
                            ? 'neu – wird vorgeschlagen'
                            : 'unbekannt'}
                        </span>
                      </>
                    )}
                  </td>
                  <td>{z.bestand === null ? '–' : mengeText(z.bestand)}</td>
                  <td className="menge">
                    <input
                      inputMode="decimal"
                      defaultValue={mengeText(z.menge)}
                      key={`${z.code}-${String(z.menge)}`}
                      onBlur={(e) => {
                        const m = leseMenge(e.target.value);
                        if (m !== null) {
                          setErfassung((alt) => mengeSetzen(alt, z.code, m));
                        }
                      }}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="taste zweit gefahr"
                      onClick={() => {
                        setErfassung((alt) => zeileEntfernen(alt, z.code));
                      }}
                    >
                      Entfernen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="zeile">
          <button
            type="button"
            className="taste"
            disabled={laeuft || erfassung.zeilen.length === 0}
            onClick={() => void buchen()}
          >
            {erfassung.art === 'wareneingang' ? 'Wareneingang buchen' : 'Inventur buchen'}
          </button>
          <button
            type="button"
            className="taste zweit"
            disabled={laeuft || erfassung.zeilen.length === 0}
            onClick={() => {
              if (window.confirm('Liste verwerfen?')) {
                setErfassung({ ...erfassung, bezug: '', zeilen: [] });
              }
            }}
          >
            Liste verwerfen
          </button>
        </div>
      </div>

      <LagerBuchungen
        auftraege={schlange.auftraege}
        sendet={schlange.sendet}
        dauerhaft={schlange.dauerhaft}
        onSenden={(id) => void schlange.senden(id)}
        onVerwerfen={(a) => void schlange.verwerfen(a)}
        onFehlerZurueck={(a) => {
          if (gesperrt) {
            setFehler('Die Liste ist in einem anderen Fenster geöffnet.');
            return;
          }
          // Erst die Liste samt Übernahmekennung dauerhaft speichern, dann die Übernahme
          // bestätigen (Codex-Befund Integration): sonst gingen die Fehlerzeilen bei einem
          // Speicherfehler verloren, ohne erneut angeboten zu werden.
          const neu = fehlerZurueckEinmal(
            erfassung,
            a.inhalt.vorgangId,
            fehlerzeilen(a),
            a.inhalt.laufAnker,
          );
          try {
            speichereErfassung(localStorage, kassenId, neu);
          } catch (e: unknown) {
            setFehler(
              `Liste nicht gespeichert – Fehlerzeilen bleiben in der Buchung: ${e instanceof Error ? e.message : String(e)}`,
            );
            return;
          }
          setErfassung(neu);
          void schlange.fehlerUebernommen(a);
        }}
      />

      <Vorschlaege
        vorschlaege={vorschlaege}
        gruppen={gruppen}
        gruppenUst={gruppenUst}
        mitEinstand
        uebernehmen={(id, daten) => api.vorschlagUebernehmen(kassenId, id, daten)}
        verwerfen={(id) => api.vorschlagVerwerfen(kassenId, id)}
        onGeaendert={() => void vorschlaegeLaden()}
      />

      {kamera && (
        <KameraScanner
          onCode={(ean) => void erfasse(ean)}
          onSchliessen={() => {
            setKamera(false);
            codeFeld.current?.focus();
          }}
        />
      )}
    </>
  );
}

/** Vorschlag, wie ihn Kasse (mit Einstandspreis) und Handterminal (ohne) bekommen. */
export interface VorschlagZeile {
  id: number;
  ean: string;
  bezeichnung: string | null;
  menge: number;
  bezug: string | null;
  einstandspreisCent?: number | null;
}

/** Offene Artikelvorschläge: mit Gruppe, Preis und Steuersatz als Artikel übernehmen. */
export function Vorschlaege(props: {
  vorschlaege: VorschlagZeile[];
  gruppen: string[];
  gruppenUst: Record<string, string | null>;
  /** Einkaufspreis erfassen (Kasse); am Handterminal bleiben Einstandspreise im Backoffice. */
  mitEinstand: boolean;
  uebernehmen: (id: number, daten: VorschlagUebernahme) => Promise<{ bestand: number }>;
  verwerfen: (id: number) => Promise<unknown>;
  onGeaendert: () => void;
}) {
  const [offen, setOffen] = useState<number | null>(null);
  const [gruppe, setGruppe] = useState('');
  const [bezeichnung, setBezeichnung] = useState('');
  const [preis, setPreis] = useState('');
  const [einstand, setEinstand] = useState('');
  const [steuersatz, setSteuersatz] = useState<Steuersatz>('20');
  const ustAus = (gruppeName: string): Steuersatz | null => {
    const s = props.gruppenUst[gruppeName];
    return STEUERSAETZE.some((x) => x.satz === s) ? (s as Steuersatz) : null;
  };
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  // K-7: gleiche Übernahme nach Netzfehler mit derselben Kennung wiederholen
  const kennung = useRef(new VorgangsKennung());

  if (props.vorschlaege.length === 0 && meldung === null) {
    return null;
  }

  function oeffnen(v: VorschlagZeile): void {
    setOffen(v.id);
    setBezeichnung(v.bezeichnung ?? '');
    setGruppe(props.gruppen[0] ?? '');
    setSteuersatz(ustAus(props.gruppen[0] ?? '') ?? '20');
    setPreis('');
    setEinstand(
      v.einstandspreisCent === null || v.einstandspreisCent === undefined
        ? ''
        : formatiereEuro(v.einstandspreisCent),
    );
    setFehler(null);
  }

  async function uebernehmen(v: VorschlagZeile): Promise<void> {
    const bruttoCent = parseEuro(preis);
    const einstandCent = einstand.trim() === '' ? null : parseEuro(einstand);
    if (bezeichnung.trim() === '' || gruppe.trim() === '') {
      setFehler('Bezeichnung und Warengruppe angeben');
      return;
    }
    if (
      bruttoCent === null ||
      bruttoCent < 0 ||
      (einstand.trim() !== '' && einstandCent === null)
    ) {
      setFehler('Preise in Euro angeben, z. B. 2,49');
      return;
    }
    setLaeuft(true);
    try {
      const daten = {
        gruppe: gruppe.trim(),
        bezeichnung: bezeichnung.trim(),
        bruttoCent,
        steuersatz,
        ...(props.mitEinstand ? { einstandspreisCent: einstandCent } : {}),
      };
      const r = await props.uebernehmen(v.id, {
        ...daten,
        vorgangId: kennung.current.fuer({ id: v.id, daten }),
      });
      kennung.current.erledigt();
      setOffen(null);
      setFehler(null);
      setMeldung(`„${bezeichnung.trim()}“ angelegt, Bestand ${mengeText(r.bestand)}`);
      props.onGeaendert();
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="karte">
      <h2>Neue Artikel aus dem Wareneingang ({props.vorschlaege.length})</h2>
      <p className="hinweis">
        Diese Codes kennt der Artikelstamm noch nicht. Übernehmen legt den Artikel an und bucht die
        gesammelte Menge als Wareneingang.
      </p>
      {props.vorschlaege.length > 0 && (
        <table className="liste">
          <thead>
            <tr>
              <th>Code</th>
              <th>Bezeichnung</th>
              <th>Menge</th>
              <th>Lieferschein</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {props.vorschlaege.map((v) => (
              <tr key={v.id}>
                <td>{v.ean}</td>
                <td>{v.bezeichnung ?? '–'}</td>
                <td>{mengeText(v.menge)}</td>
                <td>{v.bezug ?? '–'}</td>
                <td>
                  <button
                    type="button"
                    className="taste zweit"
                    onClick={() => {
                      oeffnen(v);
                    }}
                  >
                    Übernehmen …
                  </button>{' '}
                  <button
                    type="button"
                    className="taste zweit gefahr"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Vorschlag ${v.ean} verwerfen? Die Menge wird nicht gebucht.`,
                        )
                      ) {
                        void props
                          .verwerfen(v.id)
                          .then(props.onGeaendert)
                          .catch((e: unknown) => {
                            setFehler(e instanceof Error ? e.message : String(e));
                          });
                      }
                    }}
                  >
                    Verwerfen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {offen !== null &&
        props.vorschlaege
          .filter((v) => v.id === offen)
          .map((v) => (
            <form
              key={v.id}
              className="zeile"
              style={{ marginTop: '0.75rem', alignItems: 'flex-end' }}
              onSubmit={(e) => {
                e.preventDefault();
                void uebernehmen(v);
              }}
            >
              <label style={{ flex: 2 }}>
                Bezeichnung
                <input
                  value={bezeichnung}
                  maxLength={120}
                  autoFocus
                  onChange={(e) => {
                    setBezeichnung(e.target.value);
                  }}
                />
              </label>
              <label style={{ flex: 1 }}>
                Warengruppe
                <input
                  list="lager-gruppen"
                  value={gruppe}
                  maxLength={80}
                  placeholder="bestehend oder neu"
                  onChange={(e) => {
                    setGruppe(e.target.value);
                    // Bekannte Gruppe: deren USt-Vorschlag übernehmen
                    const ust = ustAus(e.target.value);
                    if (ust !== null) {
                      setSteuersatz(ust);
                    }
                  }}
                />
                <datalist id="lager-gruppen">
                  {props.gruppen.map((g) => (
                    <option key={g} value={g} />
                  ))}
                </datalist>
              </label>
              <label>
                Verkaufspreis brutto €
                <input
                  inputMode="decimal"
                  value={preis}
                  placeholder="2,49"
                  onChange={(e) => {
                    setPreis(e.target.value);
                  }}
                />
              </label>
              <label>
                Steuersatz
                <select
                  value={steuersatz}
                  onChange={(e) => {
                    setSteuersatz(e.target.value as Steuersatz);
                  }}
                >
                  {STEUERSAETZE.map((s) => (
                    <option key={s.satz} value={s.satz}>
                      {s.text}
                    </option>
                  ))}
                </select>
              </label>
              {props.mitEinstand && (
                <label>
                  Einkauf netto €
                  <input
                    inputMode="decimal"
                    value={einstand}
                    placeholder="optional"
                    onChange={(e) => {
                      setEinstand(e.target.value);
                    }}
                  />
                </label>
              )}
              <button type="submit" className="taste" disabled={laeuft}>
                Artikel anlegen
              </button>
              <button
                type="button"
                className="taste zweit"
                onClick={() => {
                  setOffen(null);
                }}
              >
                Abbrechen
              </button>
            </form>
          ))}
      {fehler !== null && <p className="fehler">{fehler}</p>}
      {meldung !== null && <p className="hinweis">{meldung}</p>}
    </div>
  );
}
